// components/Remote.tsx (Main component)
import React, { useState, useEffect, useCallback, useRef } from "react";
import { IonPage, IonContent } from "@ionic/react";
import WebWarningModal from "../../components/ui/WebWarningModal";
import { IS_WEB_BUILD, WEB_REMOTE_ENABLED } from "../../utils/constants";
import "./Remote.css";
import { useMediaQuery, useHeightQuery } from "../../utils/useMediaQuery";
import RemoteContent from "./subcompoenents/RemoteContent";
import RemoteHeader from "./subcompoenents/RemoteHeader";
import { useBLE } from "../../hooks/useBle";
import { RequestMessageType } from "../../contexts/types";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { useHistory } from "react-router-dom";

const Remote: React.FC = () => {
  const [playButtonScale, setPlayButtonScale] = useState(1);
  // Web + kill switch off = feature is unavailable.
  const webRemoteBlocked = IS_WEB_BUILD && !WEB_REMOTE_ENABLED;
  const [showWebWarning, setShowWebWarning] = useState(webRemoteBlocked);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const isTablet = useMediaQuery("(min-width: 1024px)");
  const isShortHeight = useHeightQuery("(max-height: 700px)");
  const history = useHistory();

  // Web: scroll fallback, on only when content is measured to overflow.
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  const {
    mode,
    therapyState,
    time,
    toggleTherapyState,
    handleFindDevice,
    // askForPressureGaugeData,
    askForSelectiveData,
    isConnected,
    device,
    parseDeviceString,
  } = useBLE();

  const handlePlayButtonScaleChange = useCallback((scale: number) => {
    setPlayButtonScale(scale);
  }, []);

  useEffect(() => {
    const askForTherapyData = async () => {
      mode === "Auto"
        ? await askForSelectiveData([
            RequestMessageType.LOGS,
            RequestMessageType.MAPS_FOR_LOGS,
            RequestMessageType.MIE_DATA,
            RequestMessageType.PRESSURE_GAUGE,
          ])
        : await askForSelectiveData([
            RequestMessageType.LOGS,
            RequestMessageType.MAPS_FOR_LOGS,
            RequestMessageType.MANUAL_MODE_TIMES,
            RequestMessageType.PRESSURE_GAUGE,
          ]);
    };
    if (isConnected) {
      askForTherapyData();
    }
  }, [mode, isConnected]);

  useEffect(() => {
    const lockOrientation = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await ScreenOrientation.lock({ orientation: "portrait" });
        } catch (error) {
          console.warn("Failed to lock orientation:", error);
        }
      }
    };
    lockOrientation();
  }, []);
  // console.log("Rendering Remote", new Date().toISOString());

  useEffect(() => {
    if (!IS_WEB_BUILD || !scrollWrapperRef.current) return;
    const wrapper = scrollWrapperRef.current;
    const content = wrapper.querySelector<HTMLElement>(".remote-container");
    if (!content) return;

    // +1px tolerance for sub-pixel rounding.
    const checkOverflow = () =>
      setNeedsScroll(content.scrollHeight > wrapper.clientHeight + 1);

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(wrapper);
    observer.observe(content);
    return () => observer.disconnect();
  }, [mode, isShortHeight, isMobile, isTablet, isConnected]);

  const isClearDevice =
    isConnected &&
    parseDeviceString(device?.device.name || "").deviceType === "BiWaze® Clear";

  const contentPadding = isShortHeight
    ? { paddingTop: "16px", paddingBottom: "60px" }
    : { paddingTop: "40px" };

  return (
    <IonPage
      className={`remote-page${IS_WEB_BUILD ? " web-remote-page" : ""}`}
    >
      {webRemoteBlocked && (
        <WebWarningModal
          isOpen={showWebWarning}
          title="Not Available on Web"
          message="The BiWaze® remote control uses Bluetooth to connect to your device. Please open Arc Connect on your iOS or Android phone to use this feature."
          ctaText="Go to Home"
          hideCloseButton
          onClose={() => {
            setShowWebWarning(false);
            history.replace("/home");
          }}
        />
      )}
      <IonContent
        className="ion-padding"
        scrollY={false}
        style={contentPadding}
      >
        {/* Full-height flex column so RemoteContent can fill remaining space */}
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <RemoteHeader
            isShortHeight={isShortHeight}
            handleFindDevice={handleFindDevice}
            title={
              isConnected
                ? `${
                    parseDeviceString(device?.device.name || "").deviceType
                  } Remote`
                : "BiWaze® System Remote"
            }
          />

          {/* Container with left border line — flex:1 fills space below header */}
          <div
            ref={scrollWrapperRef}
            style={{
              position: "relative",
              paddingLeft: "18px",
              flex: 1,
              overflow: IS_WEB_BUILD && needsScroll ? "auto" : "hidden",
            }}
          >
            {/* Gradient line */}
            <div
              style={{
                position: "absolute",
                left: "0",
                top: "0",
                bottom: "0",
                width: "4px",
                background: "linear-gradient(90deg, #01b7c4 0%, #00acd7 100%)",
              }}
            />
            <RemoteContent
              isAuto={mode === "Auto"}
              pauseTime={time}
              isPlaying={therapyState}
              isShortHeight={isShortHeight}
              isMobile={isMobile}
              isTablet={isTablet}
              playButtonScale={playButtonScale}
              isClearDevice={isClearDevice}
              // onModeToggle={handleModeToggle}
              // onPauseTimeChange={handlePauseTimeChange}
              onPlayToggle={async () => {
                await toggleTherapyState();
              }}
              onPlayButtonScaleChange={handlePlayButtonScaleChange}
            />
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Remote;
